import React from 'react';

import parse from 'date-fns/parse';

import { formatNumber, LinkWrapper } from '@ott/activity-card';
import { formatPeriod } from '@ott/formatted-period';
import { formatMessage } from '@ott/l10n';

import { WidgetProps } from '../../ActivitiesWidget';
import { SearchResult } from '../../effects/useActivitiesSearch';
import trackKm from '../../helpers/trackKm';

import styles from './WidgetHeader.scss';

const parseDate = (date: string) => parse(date, 'yyyyMMdd', new Date());

type Props = Pick<
  WidgetProps,
  | 'isSearchLinkVisible'
  | 'isSubtitleVisible'
  | 'shouldOpenInNewTab'
  | 'shouldUseExternalLinks'
  | 'subtitleActivitiesTemplate'
  | 'title'
  | 'trackId'
  | 'trackPage'
> &
  Pick<SearchResult, 'isLoading' | 'meta'> & { prepareSearchLink: () => string };

const WidgetHeader = ({
  isLoading,
  isSearchLinkVisible,
  isSubtitleVisible,
  meta,
  prepareSearchLink,
  shouldOpenInNewTab,
  shouldUseExternalLinks,
  subtitleActivitiesTemplate,
  title,
  trackId,
  trackPage,
}: Props) => {
  const { localityCount, localityId, startDate, endDate } = meta;

  const renderSubtitle = () => {
    if (!isSubtitleVisible || isLoading) {
      return null;
    }

    return (
      <div data-locator="subtitle">
        {formatNumber(localityCount)}&nbsp;
        {formatMessage(
          {
            id: 'activitiesWidget.activities',
            defaultMessage: subtitleActivitiesTemplate,
          },
          {
            count: localityCount,
          }
        )}
        {startDate && (
          <>
            {' на '}
            <span className={styles.nowrap}>
              {formatPeriod({
                startDate: parseDate(startDate),
                endDate: parseDate(endDate || startDate),
                foldSameDate: true,
              })}
            </span>
          </>
        )}
      </div>
    );
  };

  const trackSearchLinkShow = () => {
    trackKm.onSearchLinkShow({
      id: trackId,
      localityId,
      page: trackPage,
    });
  };

  const trackSearchLinkClick = () => {
    trackKm.onSearchLinkClick({
      id: trackId,
      localityId,
      page: trackPage,
    });
  };

  const renderSearchLink = () => {
    if (!isSearchLinkVisible || isLoading) {
      return null;
    }

    return (
      <LinkWrapper
        className={styles.searchLink}
        isExternalLink={shouldUseExternalLinks}
        openInNewTab={shouldOpenInNewTab}
        specificLocators="search-link"
        to={prepareSearchLink()}
        onMount={() => trackSearchLinkShow()}
        onClick={() => trackSearchLinkClick()}
      >
        Смотреть все
      </LinkWrapper>
    );
  };

  const subtitle = renderSubtitle();
  const searchLink = renderSearchLink();

  return (
    <div className={styles.header} data-locator="header">
      <div className={styles.headerRow}>
        <div className={styles.title}>{title}</div>
        {!subtitle && searchLink}
      </div>

      {subtitle && (
        <div className={styles.headerRow}>
          <div className={styles.subtitle}>{subtitle}</div>
          {searchLink}
        </div>
      )}
    </div>
  );
};

export default WidgetHeader;
